import { useState, useEffect } from 'react';
import { Search, Bell, MessageSquare, X, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AgentInteraction, IntentLevel } from '../../types/agent';
import { Badge, EmptyState } from '../shared';

type FilterType = 'all' | 'chatgpt' | 'claude' | 'gemini' | 'perplexity' | 'copilot';
type SortField = 'timestamp' | 'intentLevel' | 'aiSystem';
type SortDirection = 'asc' | 'desc';

export function InteractionLog() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [interactions, setInteractions] = useState<AgentInteraction[]>([]);
  const [filteredInteractions, setFilteredInteractions] = useState<AgentInteraction[]>([]);
  const [filterSystem, setFilterSystem] = useState<FilterType>('all');
  const [filterIntent, setFilterIntent] = useState<IntentLevel | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedInteraction, setSelectedInteraction] = useState<string | null>(null);

  useEffect(() => {
    loadInteractions();
  }, [user]);

  useEffect(() => {
    filterAndSortInteractions();
  }, [interactions, filterSystem, filterIntent, searchTerm, sortField, sortDirection]);

  const loadInteractions = async () => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'agentInteractions'),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(500)
      );
      const snapshot = await getDocs(q);
      setInteractions(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }) as AgentInteraction));
    } catch (error) {
      console.error('Error loading interactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortInteractions = () => {
    let filtered = interactions;

    // Apply system filter
    if (filterSystem !== 'all') {
      filtered = filtered.filter(i => i.aiSystem === filterSystem);
    }

    // Apply intent filter
    if (filterIntent !== 'all') {
      filtered = filtered.filter(i => i.intentLevel === filterIntent);
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(i =>
        i.query.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.response.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      if (sortField === 'timestamp') {
        aVal = aVal.toDate().getTime();
        bVal = bVal.toDate().getTime();
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredInteractions(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const uniqueSystems = Array.from(new Set(interactions.map(i => i.aiSystem)));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t3-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Agent</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Interaction Log</span>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search interactions..."
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 w-64 focus:outline-none focus:border-t3-primary"
            />
          </div>
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        <div className="mb-6">
          <p className="text-xs font-semibold text-t3-primary uppercase tracking-wide mb-1">Kodiac Agent</p>
          <h1 className="text-3xl font-semibold text-gray-900">Interaction Log</h1>
          <p className="text-sm text-gray-600 mt-1">Complete history of agent interactions across all platforms</p>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className="text-sm text-gray-700 font-medium">Filter:</span>

          {/* AI System Filters */}
          {uniqueSystems.map(system => (
            <button
              key={system}
              onClick={() => setFilterSystem(filterSystem === system as FilterType ? 'all' : system as FilterType)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterSystem === system
                  ? 'bg-t3-primary text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {system}
              {filterSystem === system && <X className="w-3 h-3 inline ml-1" />}
            </button>
          ))}

          {/* Intent Level Filters */}
          {(['high', 'medium', 'low'] as IntentLevel[]).map(intent => (
            <button
              key={intent}
              onClick={() => setFilterIntent(filterIntent === intent ? 'all' : intent)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filterIntent === intent
                  ? 'bg-t3-primary text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {intent} intent
              {filterIntent === intent && <X className="w-3 h-3 inline ml-1" />}
            </button>
          ))}

          {(filterSystem !== 'all' || filterIntent !== 'all') && (
            <button
              onClick={() => {
                setFilterSystem('all');
                setFilterIntent('all');
              }}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Clear all
            </button>
          )}
        </div>

        {/* Results Count */}
        <p className="text-sm text-gray-700 mb-4">
          {filteredInteractions.length} of {interactions.length} interactions
        </p>

        {/* Interactions Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {filteredInteractions.length === 0 ? (
            <div className="p-12">
              <EmptyState
                icon={MessageSquare}
                title={filterSystem !== 'all' || filterIntent !== 'all' ? 'No matching interactions' : 'No interactions yet'}
                description={
                  filterSystem !== 'all' || filterIntent !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Interactions will appear here as users engage with your agent'
                }
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-warm border-b border-gray-200">
                  <tr>
                    <th
                      onClick={() => handleSort('timestamp')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Time
                        {sortField === 'timestamp' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th
                      onClick={() => handleSort('aiSystem')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        AI System
                        {sortField === 'aiSystem' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Query
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Type
                    </th>
                    <th
                      onClick={() => handleSort('intentLevel')}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Intent
                        {sortField === 'intentLevel' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Guardrails
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInteractions.map(interaction => (
                    <tr key={interaction.id} className="hover:bg-surface-warm transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">
                          {interaction.timestamp.toDate().toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-600">
                          {interaction.timestamp.toDate().toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="primary">{interaction.aiSystem}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {interaction.query}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="primary">{interaction.type}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        {interaction.intentLevel !== 'none' ? (
                          <Badge
                            variant={
                              interaction.intentLevel === 'high'
                                ? 'critical'
                                : interaction.intentLevel === 'medium'
                                ? 'warning'
                                : 'primary'
                            }
                          >
                            {interaction.intentLevel}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {interaction.guardrailsTriggered.length > 0 ? (
                          <span className="text-sm text-warning-amber">
                            {interaction.guardrailsTriggered.length}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedInteraction(
                            selectedInteraction === interaction.id ? null : interaction.id
                          )}
                          className="flex items-center gap-1 text-sm text-t3-primary hover:text-purple-700"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedInteraction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Interaction Details</h2>
                  <button
                    onClick={() => setSelectedInteraction(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {(() => {
                  const interaction = interactions.find(i => i.id === selectedInteraction);
                  if (!interaction) return null;

                  return (
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Query</p>
                        <p className="text-sm text-gray-900 bg-surface-warm p-3 rounded-lg">
                          {interaction.query}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Response</p>
                        <p className="text-sm text-gray-900 bg-surface-warm p-3 rounded-lg whitespace-pre-wrap">
                          {interaction.response}
                        </p>
                      </div>

                      {interaction.intentSignals.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            Intent Signals ({interaction.intentSignals.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {interaction.intentSignals.map((signal, idx) => (
                              <span key={idx} className="px-2 py-1 bg-purple-100 text-t3-primary rounded text-sm">
                                {signal}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {interaction.guardrailsTriggered.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">
                            Guardrails Triggered ({interaction.guardrailsTriggered.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {interaction.guardrailsTriggered.map((rule, idx) => (
                              <span key={idx} className="px-2 py-1 bg-yellow-100 text-warning-amber rounded text-sm">
                                {rule}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">AI System</p>
                          <Badge variant="primary">{interaction.aiSystem}</Badge>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Type</p>
                          <Badge variant="primary">{interaction.type}</Badge>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Intent Level</p>
                          <Badge
                            variant={
                              interaction.intentLevel === 'high'
                                ? 'critical'
                                : interaction.intentLevel === 'medium'
                                ? 'warning'
                                : 'primary'
                            }
                          >
                            {interaction.intentLevel}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Escalated</p>
                          <Badge variant={interaction.escalated ? 'critical' : 'success'}>
                            {interaction.escalated ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
